import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, ViewEncapsulation, Input, EventEmitter, Output, NgModule } from '@angular/core';
import { Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { e11FieldInputDynamicComponent, e11FieldInputAutocomplete, e11FormEventHelper, e11FieldInputText, e11FieldInlineInputSelect, e11FieldInputSelect, e11FormLayoutInline3Grid } from '@engineering11/ui-lib/e11-form-builder';
import { E11InlineAutocompleteComponent } from '@engineering11/ui-lib/e11-inline-autocomplete';
import { E11InlineInputComponent } from '@engineering11/ui-lib/e11-inline-input';
import { E11FormValidator } from '@engineering11/ui-lib/e11-input-errors';
import { isNotNil } from '@engineering11/utility';
import { formSelectOptionsCountry } from '@engineering11/web-utilities';
import { BehaviorSubject, of, from, switchMap as switchMap$1, take, tap as tap$1, map as map$1, filter as filter$1, Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import * as i2 from '@engineering11/web-api-error';
import { toE11Error, e11Error } from '@engineering11/web-api-error';
import { tap, map, catchError, filter, switchMap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { CollectionRepository } from '@engineering11/web-api-firebase';
import * as i4 from '@angular/common';
import { NgIf, NgForOf, CommonModule } from '@angular/common';
import * as i3 from '@angular/google-maps';
import { GoogleMapsModule } from '@angular/google-maps';

const LOCATION_ERRORS = Object.freeze({
    PERMISSION_DENIED: 'location/permission-denied',
    POSITION_UNAVAILABLE: 'location/position-unavailable',
    TIMEOUT: 'location/position-unavailable',
    NO_BROWSER_SUPPORT: 'location/no-browser-support',
    UNKNOWN: 'location/unknown',
});
function toE11GeolocationError(err) {
    if (err.code === err.PERMISSION_DENIED) {
        return toE11Error(new Error('PERMISSION_DENIED error getting geolocation'), {
            type: LOCATION_ERRORS.PERMISSION_DENIED,
            title: 'Please grant location permission',
            message: 'Permission denied getting location. Please grant location permission',
        });
    }
    else if (err.code === err.POSITION_UNAVAILABLE) {
        return toE11Error(new Error('POSITION_UNAVAILABLE error getting geolocation'), {
            type: LOCATION_ERRORS.POSITION_UNAVAILABLE,
            title: 'Position unavailable',
            message: 'Position unavailable',
        });
    }
    else if (err.code === err.TIMEOUT) {
        return toE11Error(new Error('TIMEOUT error getting geolocation'), {
            type: LOCATION_ERRORS.TIMEOUT,
            title: 'Timeout getting location. Please try again',
            message: 'Timeout getting location.',
        });
    }
    else {
        return toE11Error(new Error('Error getting geolocation'), {
            type: LOCATION_ERRORS.UNKNOWN,
            title: 'Unknown error getting location',
            message: 'Unknown error getting location',
        });
    }
}

const MAPS_CONFIG = new InjectionToken('MapsConfig');

/**
 * Purpose of this service is to lazy-load the google maps API and ensure that it is only called once.
 * This should start loading maps when it is first used in a component
 */
class MapsLoaderService {
    constructor(httpClient, mapsConfig, logger) {
        this.httpClient = httpClient;
        this.mapsConfig = mapsConfig;
        this.logger = logger;
        this.mapsLoaded = new BehaviorSubject(false);
        if (window.google && window.google.maps) {
            // Google maps already loaded on the page.
            this.mapsLoaded.next(true);
        }
        else if (this.mapsConfig.mapsApiKey) {
            // TODO: retry with exponential backoff?
            this.httpClient
                .jsonp(`https://maps.googleapis.com/maps/api/js?key=${this.mapsConfig.mapsApiKey}&libraries=places`, 'callback')
                .pipe(tap((mapsRes) => this.logger.log('Maps loaded!')), map(() => true), catchError((err) => {
                this.logger.error(err);
                return of(false);
            }))
                .subscribe((loaded) => {
                this.mapsLoaded.next(loaded);
            });
        }
        else {
            this.logger.warn('No Maps API Key Configured. Configure an API key.');
            this.mapsLoaded.next(false);
        }
    }
    loadMaps() {
        return this.mapsLoaded;
        // // TODO: retry with exponential backoff?
        // const mapsCall = this.httpClient
        //   .jsonp(`https://maps.googleapis.com/maps/api/js?key=${this.mapsConfig.mapsApiKey}`, 'callback')
        //   .pipe(tap((mapsRes) => this.logger.log('Maps loaded!')))
        // if (this.loaded) {
        //   return of(true)
        // } else {
        //   return mapsCall.pipe(
        //     map(() => true),
        //     catchError((err) => {
        //       this.logger.error(err)
        //       return of(false)
        //     }),
        //     tap(() => (this.loaded = true))
        //   )
        // }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsLoaderService, deps: [{ token: i1.HttpClient }, { token: MAPS_CONFIG }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsLoaderService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAPS_CONFIG]
                }] }, { type: i2.E11Logger }] });

/**
 * This service should be kept internal to the maps SDK - it can be wrapped by a higher level service
 * TODO: Consider switching from https://ipapi.co/json/ to our own endpoint using IPLookUpService
 */
class IpLocationService {
    constructor(logger) {
        this.logger = logger;
    }
    async getLocationFromIP() {
        try {
            const locationData = await this.getIpLocationData();
            this.logger.log('IpLocationService - getLocationFromIP', locationData);
            return apiDataToApproxLocation(locationData);
        }
        catch (err) {
            throw toE11Error(err, { type: 'maps/location-from-ip-error', title: 'Error getting your approximate location' });
        }
    }
    async getIpLocationData() {
        /**
         * Uses Engineering11 API key managed by service@engineering11.com
         * Checking a key into source control is not usually recommended, but for this service was judged to be low risk since it only does one simple thing and is read-only
         */
        const locationData = await fetch('https://ipapi.co/json/?key=QsyelvSd61lyGW98VnQ1uPrCxb0eQfCh2rExFUJXUFjTktEuwQ');
        return locationData.json();
    }
    async getIP() {
        // https://www.ipify.org/ open source IP fetcher
        const ipAddress = await fetch('https://api.ipify.org/?format=json');
        const ipJSON = await ipAddress.json();
        this.logger.log({ ipJSON });
        return ipJSON.id;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: IpLocationService, deps: [{ token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: IpLocationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: IpLocationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i2.E11Logger }] });
function apiDataToApproxLocation(apiData) {
    const { city, region, country } = apiData;
    return { lat: apiData.latitude, lng: apiData.longitude, city, region, country };
}

class LocationService {
    constructor(logger, e11ErrorHandler, mapsLoaderService, ipLocationService) {
        this.logger = logger;
        this.e11ErrorHandler = e11ErrorHandler;
        this.mapsLoaderService = mapsLoaderService;
        this.ipLocationService = ipLocationService;
    }
    /**
     * This requests permission from the user and gets their exact geolocation
     * @returns @IGeoLocation
     */
    getGeolocation() {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    resolve(pos);
                }, (err) => {
                    reject(toE11GeolocationError(err));
                });
            }
            else {
                reject(toE11Error(new Error('Browser does not support geolocation'), {
                    type: LOCATION_ERRORS.NO_BROWSER_SUPPORT,
                    title: 'Browser does not support location. Please use a different browser',
                    message: 'Browser does not support location.',
                }));
            }
        });
    }
    /**
     *
     */
    async getApproximateLocation() {
        return this.ipLocationService.getLocationFromIP();
    }
    getLocationFromPostcode(postcode) {
        return this.mapsLoaderService.loadMaps().pipe(tap((loaded) => this.logger.log({ loaded })), filter((loaded) => loaded), switchMap(() => {
            const geocoder = new google.maps.Geocoder();
            return from(new Promise((resolve, reject) => {
                geocoder.geocode({ address: postcode }, (result, status) => {
                    if (status === google.maps.GeocoderStatus.ZERO_RESULTS)
                        return reject(e11Error(postcodeNotFoundError({ postcode, result, status })));
                    if (status !== google.maps.GeocoderStatus.OK || !result)
                        return reject(e11Error(locationError({ postcode, result, status })));
                    this.logger.log({ result, status });
                    const topResult = result[0];
                    const latLng = topResult.geometry.location.toJSON();
                    const bounds = topResult.geometry.viewport.toJSON();
                    this.logger.log({ latLng, bounds });
                    resolve(latLng);
                });
            }));
        }));
    }
    getLocationPredictions(address) {
        return this.mapsLoaderService.loadMaps().pipe(tap((loaded) => this.logger.log({ loaded })), filter((loaded) => loaded), switchMap(() => {
            const autocompleteService = new google.maps.places.AutocompleteService();
            return from(new Promise((resolve, reject) => {
                autocompleteService.getPlacePredictions({ input: address }, (result, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS)
                        return resolve([]);
                    if (status !== google.maps.places.PlacesServiceStatus.OK || !result)
                        return reject(e11Error(autocompleteError({ result, status, query: address })));
                    this.logger.log('LocationService - getLocationPredictions -', { result, status, query: address });
                    const predictions = result.map((prediction) => ({
                        description: prediction.description,
                        placeId: prediction.place_id,
                    }));
                    resolve(predictions);
                });
            }));
        }));
    }
    getLocationDetails(placeId) {
        return this.mapsLoaderService.loadMaps().pipe(filter((loaded) => loaded), switchMap(() => {
            const placeService = new google.maps.places.PlacesService(document.createElement('div'));
            return from(new Promise((resolve, reject) => {
                placeService.getDetails({ placeId }, (result, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS)
                        return resolve({});
                    if (status !== google.maps.places.PlacesServiceStatus.OK || !result)
                        return reject(e11Error(placeDetailError({ result, status, placeId })));
                    this.logger.log('LocationService - getLocationDetails -', { result, status, placeId });
                    resolve(this.parseAddressComponents(result));
                });
            }));
        }));
    }
    parseAddressComponents(placeResult) {
        const placeDetails = {};
        placeDetails.latitude = placeResult.geometry?.location?.lat();
        placeDetails.longitude = placeResult.geometry?.location?.lng();
        placeDetails.name = placeResult.name;
        placeResult.address_components?.forEach((component) => {
            component.types.forEach((type) => {
                const key = this.mapAddressComponent(type);
                if (!key || placeDetails[key])
                    return;
                placeDetails[key] = component.long_name; // use any to avoid type error
            });
        });
        return placeDetails;
    }
    mapAddressComponent(componentType) {
        const componentTypeMap = {
            street_number: 'streetNumber',
            route: 'street',
            city: 'city',
            locality: 'city',
            postal_town: 'city',
            administrative_area_level_1: 'state',
            country: 'country',
            postal_code: 'postalCode',
        };
        return componentTypeMap[componentType] || null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationService, deps: [{ token: i2.E11Logger }, { token: i2.E11ErrorHandlerService }, { token: MapsLoaderService }, { token: IpLocationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i2.E11Logger }, { type: i2.E11ErrorHandlerService }, { type: MapsLoaderService }, { type: IpLocationService }] });
const autocompleteError = (additionalData) => ({
    title: 'Failed to get location predictions',
    type: 'maps/google-autocomplete',
    additionalData: additionalData,
});
const placeDetailError = (additionalData) => ({
    title: 'Failed to get place details',
    type: 'maps/google-place-details',
    additionalData: additionalData,
});
const postcodeNotFoundError = (additionalData) => ({
    title: `Post code ${additionalData.postcode} not found`,
    type: 'maps/google-geocoder',
    additionalData: additionalData,
    level: 'none', // User presumably entered an invalid postcode
});
const locationError = (additionalData) => ({
    title: 'Failed to get location',
    type: 'maps/google-geocoder',
    additionalData: additionalData,
});

const initialState$1 = {
    approximateLocation: null,
    exactLocation: null,
    locationPredictions: [],
    locationDetails: null,
};
class LocationStore extends ComponentStore {
    constructor(locationService, errorHandler, logger) {
        super(initialState$1);
        this.locationService = locationService;
        this.errorHandler = errorHandler;
        this.logger = logger;
        // SELECTORS
        this.getState = this.select((s) => s);
        this.approximateLocation$ = this.select((s) => s.approximateLocation);
        this.exactLocation$ = this.select((s) => s.approximateLocation);
        this.locationPredictions$ = this.select((s) => s.locationPredictions);
        this.locationDetails$ = this.select((s) => s.locationDetails);
        // EFFECTS
        /**
         * Does not require user permission
         * Will only fetch if it hasn't already been fetched
         * Use @onGetApproximateLocation to force a fetch
         */
        this.onGetApproximateLocationIfNotLoaded = this.effect((_$) => {
            return _$.pipe(switchMap$1((_) => this.approximateLocation$.pipe(take(1), tap$1((location) => {
                if (!location)
                    this.onGetApproximateLocation();
                else
                    this.logger.info('Store: approximate location already loaded, no need to re-fetch');
            }))));
        });
        this.onGetApproximateLocation = this.effect((_$) => _$.pipe(switchMap$1((_) => {
            return from(this.locationService.getApproximateLocation()).pipe(tapResponse((location) => {
                this.logger.log('LocationStore - onGetApproximateLocation -', location);
                return this.onApproximateLocationFetched(location);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        /**
         * Requires user permission.
         * Will only fetch if it hasn't already been fetched
         * Use @onGetExactLocation to force a fetch
         */
        this.onGetExactLocationIfNotLoaded = this.effect((_$) => {
            return _$.pipe(switchMap$1((_) => this.exactLocation$.pipe(take(1), tap$1((location) => {
                if (!location)
                    this.onGetExactLocation();
                else
                    this.logger.info('Store: approximate location already loaded, no need to re-fetch');
            }))));
        });
        /**
         * Requires user permission
         */
        this.onGetExactLocation = this.effect((_$) => _$.pipe(switchMap$1((_) => {
            return from(this.locationService.getGeolocation()).pipe(tapResponse((location) => {
                this.logger.log('LocationStore - onGetExactLocation -', location);
                return this.onExactLocationFetched(location);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        this.onGetLocationPredictions = this.effect((input$) => input$.pipe(switchMap$1((input) => {
            return from(this.locationService.getLocationPredictions(input)).pipe(tapResponse((predictions) => {
                this.logger.log('LocationStore - onGetLocationPredictions -', predictions);
                return this.onLocationPredictionsFetched(predictions);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        this.onGetLocationDetails = this.effect((placeId$) => placeId$.pipe(switchMap$1((placeId) => {
            return from(this.locationService.getLocationDetails(placeId)).pipe(tapResponse((details) => {
                this.logger.log('LocationStore - onGetLocationDetails -', details);
                return this.onLocationDetailsFetched(details);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        this.onResetPredictions = this.effect((_$) => _$.pipe(tap$1(() => {
            this.onLocationPredictionsFetched([]);
            this.onLocationDetailsFetched(null);
        })));
        // UPDATERS
        this.onApproximateLocationFetched = this.updater((state, approximateLocation) => ({
            ...state,
            approximateLocation,
        }));
        this.onExactLocationFetched = this.updater((state, exactLocation) => ({
            ...state,
            exactLocation,
        }));
        this.onLocationPredictionsFetched = this.updater((state, locationPredictions) => ({
            ...state,
            locationPredictions,
        }));
        this.onLocationDetailsFetched = this.updater((state, locationDetails) => ({
            ...state,
            locationDetails,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationStore, deps: [{ token: LocationService }, { token: i2.E11ErrorHandlerService }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocationStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: LocationService }, { type: i2.E11ErrorHandlerService }, { type: i2.E11Logger }] });

class CountryRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = 'countries/';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class CountryService {
    constructor(countryRepository) {
        this.countryRepository = countryRepository;
    }
    getStatesForCountry(countryCode) {
        return this.countryRepository.get(countryCode).pipe(map$1((country) => country?.states ?? []));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryService, deps: [{ token: CountryRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CountryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: CountryRepository }] });

const initialState = {
    availableStates: [],
    fetchedCountries: {},
};
class AddressFormStore extends ComponentStore {
    constructor(countryService) {
        super(initialState);
        this.countryService = countryService;
        // Selectors
        this.getState = this.select((s) => s);
        this.availableStates$ = this.select((state) => statesToOptions(state.availableStates));
        // Effects
        this.selectCountry = this.effect((country$) => country$.pipe(switchMap((country) => this.getStatesForCountry(country)), tap((states) => this.setAvailableStates(states))));
        // Updaters
        this.setAvailableStates = this.updater((state, states) => ({
            ...state,
            availableStates: states,
        }));
        this.addFetchedCountry = this.updater((state, { country, states }) => ({
            ...state,
            fetchedCountries: {
                ...state.fetchedCountries,
                [country]: states,
            },
        }));
    }
    getStatesForCountry(country) {
        return this.select((state) => state.fetchedCountries[country]).pipe(switchMap((cachedStates) => {
            if (cachedStates) {
                return of(cachedStates);
            }
            return this.countryService.getStatesForCountry(country).pipe(tap((states) => this.addFetchedCountry({ country, states })));
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormStore, deps: [{ token: CountryService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: CountryService }] });
function statesToOptions(states) {
    return states.map((state) => ({ name: state.name, value: state.code }));
}

/**
 * @class AddressFormService
 * @description A service for managing address form functionality in the application.
 *
 * @usage
 * To use this service:
 * 1. Inject the AddressFormService into your component or service.
 * 2. Call the init() method passing an instance of E11DynamicFormBuilderComponent.
 * 3. Call ngOnDestroy() method of the service in your component's ngOnDestroy lifecycle hook.
 * 4. Call formGroup() method to get the form group for the address form
 *
 * Example:
 * ```typescript
 * export class MyComponent implements OnInit, OnDestroy {
 *   @ViewChild(E11DynamicFormBuilderComponent) formBuilder!: E11DynamicFormBuilderComponent<any>;
 *
 *   formFields = {
 *     ///...existing form fields
 *     e11FormLayoutInlineGrid([
 *       ...this.addressFormService.formGroup({
 *         validators: {
 *           address: [Validators.required],
 *           city: [Validators.required],
 *         },
 *       }),
 *     ])
 *   }
 *
 *   constructor(private addressFormService: AddressFormService) {}
 *
 *   ngOnInit() {
 *     this.addressFormService.init(this.formBuilder);
 *   }
 *
 *   ngOnDestroy() {
 *     this.addressFormService.ngOnDestroy();
 *   }
 * }
 * ```
 */
class AddressFormService {
    constructor(locationStore, addressFormStore, zone) {
        this.locationStore = locationStore;
        this.addressFormStore = addressFormStore;
        this.zone = zone;
        this.locationPredictions$ = this.locationStore.locationPredictions$;
        this.selectedLocationPrediction$ = this.locationStore.locationDetails$.pipe(filter$1(isNotNil));
        this.destroy$ = new Subject();
        this.STATE_REQUIRED_LENGTH = 2;
        this.fieldFields = this.generateFormFields();
        this.availableStates = [];
        this.isInline = false;
        this.autocompleteConfigAddress = this.createAutocompleteConfig([], 'description');
        this.addressFormStore.selectCountry('US');
    }
    getDefaultFieldValidators(config) {
        const requiredValidator = config?.markAsRequired ? [Validators.required] : [];
        return {
            address: [...requiredValidator, Validators.maxLength(500)],
            address2: [Validators.maxLength(500)],
            city: [...requiredValidator, E11FormValidator.validateAddressCity()],
            state: [...requiredValidator, Validators.minLength(this.STATE_REQUIRED_LENGTH)],
            zip: [...requiredValidator],
            countryCode: [Validators.required, E11FormValidator.validateAddressCountry()],
        };
    }
    /**
     * Initialize the location form and return a function to destroy the subscriptions
     *
     * **Note:** Ensure the `OnDestroy` lifecycle hook is implemented in the parent component
     * to clean up the subscriptions when the component is destroyed
     * @param formBuilder
     */
    init(formBuilder, inlineAddressFormConfig) {
        if (!formBuilder) {
            throw new Error('Form builder is required for location form');
        }
        this.listenForAddressPrediction();
        this.listenForSelectedPredictedAddress(formBuilder);
        this.listenForStateOptions(formBuilder);
        if (inlineAddressFormConfig) {
            this.inlineAddressFormConfig = inlineAddressFormConfig;
            this.isInline = inlineAddressFormConfig.isInlineForm;
        }
        return () => this.ngOnDestroy();
    }
    /**
     * Generate the form group for the location form for use in the parent dynamic form builder
     */
    formGroup(config, formBuilder) {
        const validators = config?.validators
            ? { ...this.getDefaultFieldValidators(config), ...config.validators }
            : this.getDefaultFieldValidators(config);
        this.fieldFields = this.generateFormFields({ ...config, validators }, formBuilder);
        return this.fieldFields;
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
        this.locationStore.onResetPredictions();
    }
    addressFormField(validators = [], formBuilder) {
        this.addressField = this.isInline
            ? e11FieldInputDynamicComponent('address', 'Address', E11InlineAutocompleteComponent, undefined, {
                validatorOrOpts: validators,
                extraInputOption: {
                    value: formBuilder?.form.controls.address.value,
                    useSearchKeyAsItemKey: true,
                    ...this.getInlineExtraOptions('address', 'Address:', formBuilder?.form, (value) => this.selectLocationPrediction(value), (v) => this.onAddressChange(of(v)), (value) => value.description, this.autocompleteConfigAddress),
                },
            })
            : e11FieldInputAutocomplete('address', 'Address', [], undefined, {
                extraInputOption: {
                    debounceTime: 300,
                    searchKeyword: 'description',
                    selected: e11FormEventHelper((selected) => this.selectLocationPrediction(selected)),
                },
                valueChange: (v) => this.onAddressChange(v),
                validatorOrOpts: validators,
            });
        return this.addressField;
    }
    address2FormField(validators = [], formBuilder) {
        return this.isInline
            ? e11FieldInputDynamicComponent('address2', '', E11InlineInputComponent, undefined, {
                validatorOrOpts: validators,
                extraInputOption: {
                    value: formBuilder?.form.controls.address2.value,
                    ...this.getInlineExtraOptions('address2', 'Address 2:', formBuilder?.form, (value) => this.inlineAddressFormConfig?.onValueChange?.(value, 'address2')),
                },
            })
            : e11FieldInputText('address2', 'Address 2', undefined, {
                extraInputOption: { placeholder: 'Suite, Floor, Unit, Apt...' },
                validatorOrOpts: validators,
            });
    }
    cityFormField(validators = [], formBuilder) {
        return this.isInline
            ? e11FieldInputDynamicComponent('city', 'City', E11InlineInputComponent, undefined, {
                validatorOrOpts: validators,
                extraInputOption: {
                    value: formBuilder?.form.controls.city.value,
                    ...this.getInlineExtraOptions('city', 'City:', formBuilder?.form, (value) => this.inlineAddressFormConfig?.onValueChange?.(value, 'city')),
                },
            })
            : e11FieldInputText('city', 'City', undefined, { validatorOrOpts: validators });
    }
    stateFormField(validators = []) {
        this.stateField = this.isInline
            ? e11FieldInlineInputSelect('state', [], 'State:', undefined, {
                hideInputError: true,
                extraInputOption: {
                    placeholder: 'Empty',
                    inputSaved: e11FormEventHelper((value) => this.inlineAddressFormConfig?.onValueChange?.(value, 'state')),
                },
            })
            : e11FieldInputSelect('state', 'State/Province/Territory', [], undefined, {
                validatorOrOpts: validators,
            });
        return this.stateField;
    }
    zipFormField(validators = [], formBuilder) {
        return this.isInline
            ? e11FieldInputDynamicComponent('zip', 'Zip/Postal Code', E11InlineInputComponent, undefined, {
                validatorOrOpts: validators,
                extraInputOption: {
                    value: formBuilder?.form.controls.zip.value,
                    ...this.getInlineExtraOptions('zip', 'Zip Code:', formBuilder?.form, (value) => this.inlineAddressFormConfig?.onValueChange?.(value, 'zip')),
                },
            })
            : e11FieldInputText('zip', 'Zip/Postal Code', undefined, { validatorOrOpts: validators });
    }
    countryCodeFormField(validators = []) {
        const defaultCountry = 'US';
        return this.isInline
            ? e11FieldInlineInputSelect('countryCode', [...formSelectOptionsCountry], 'Country Code:', undefined, {
                validatorOrOpts: validators,
                hideInputError: true,
                extraInputOption: {
                    placeholder: 'Empty',
                    inputSaved: e11FormEventHelper((value) => this.inlineAddressFormConfig?.onValueChange?.(value, 'countryCode')),
                },
                valueChange: (v) => this.onCountryChange(v),
            })
            : e11FieldInputSelect('countryCode', 'Country Code', [...formSelectOptionsCountry], defaultCountry, {
                validatorOrOpts: validators,
                valueChange: (v) => this.onCountryChange(v),
            });
    }
    generateFormFields(config, formBuilder) {
        const validators = config?.validators ?? this.getDefaultFieldValidators(config);
        return [
            e11FormLayoutInline3Grid([
                this.addressFormField(validators.address, formBuilder),
                this.address2FormField(validators.address2, formBuilder),
                this.cityFormField(validators.city, formBuilder),
            ]),
            e11FormLayoutInline3Grid([
                this.stateFormField(validators.state),
                this.zipFormField(validators.zip, formBuilder),
                this.countryCodeFormField(validators.countryCode),
            ]),
        ];
    }
    selectLocationPrediction(prediction) {
        if (!prediction && this.inlineAddressFormConfig) {
            this.inlineAddressFormConfig.onValueChange(null, 'address');
            return;
        }
        this.locationStore.onGetLocationDetails(prediction.placeId);
    }
    onAddressChange(value$) {
        value$
            .pipe(filter$1((term) => term?.length > 2), debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((term) => {
            this.locationStore.onGetLocationPredictions(term);
        });
    }
    onCountryChange(value$) {
        value$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((country) => {
            this.addressFormStore.selectCountry(country);
        });
    }
    listenForSelectedPredictedAddress(formBuilderComponent) {
        this.selectedLocationPrediction$.pipe(takeUntil(this.destroy$)).subscribe((location) => {
            this.selectedLocationStateName = location.state;
            const stateCode = location.state ? this.stateNameToCode(location.state) : null;
            const resolvedData = {
                address: this.buildAddress1FromLocationDetails(location)?.trim(),
                city: location.city,
                state: stateCode,
                zip: location.postalCode,
                countryCode: location.country ? this.countryNameToCode(location.country) : null,
            };
            formBuilderComponent.form.patchValue(resolvedData);
            if (this.inlineAddressFormConfig) {
                this.inlineAddressFormConfig.onValueChange(resolvedData, 'address');
            }
            this.locationStore.onLocationDetailsFetched(null);
        });
    }
    buildAddress1FromLocationDetails(locationDetails) {
        const { name, streetNumber, street, city } = locationDetails;
        if (name)
            return name;
        const addressComponents = [streetNumber, street];
        const address = addressComponents.map((cmp) => cmp).join(', ');
        return address || city || null;
    }
    countryNameToCode(countryName) {
        return formSelectOptionsCountry.find((country) => country.name.toLowerCase() === countryName.toLowerCase())?.value ?? null;
    }
    stateNameToCode(stateName) {
        return this.availableStates.find((state) => state.name.toLowerCase() === stateName.toLowerCase())?.value ?? null;
    }
    listenForAddressPrediction() {
        this.locationPredictions$.pipe(takeUntil(this.destroy$)).subscribe((predictions) => {
            if (!this.addressField)
                return;
            this.addressField.options = predictions;
            this.autocompleteConfigAddress.data = predictions;
        });
    }
    listenForStateOptions(formBuilderComponent) {
        this.addressFormStore.availableStates$.pipe(takeUntil(this.destroy$)).subscribe((states) => {
            // Set timeout to prevent ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
                if (!this.stateField)
                    return;
                this.stateField.options = states;
                this.availableStates = states;
                if (!this.selectedLocationStateName)
                    return;
                const state = this.stateNameToCode(this.selectedLocationStateName);
                formBuilderComponent.form.patchValue({ state: state });
            });
        });
    }
    createAutocompleteConfig(initialData, searchKeyword) {
        return {
            data: initialData,
            searchKeyword,
            isLoading: false,
            isMultiSelect: false,
            selectedData: [],
        };
    }
    /**
     * Helper to build the common extra options for inline dynamic components.
     */
    getInlineExtraOptions(field, label, form, savedCallback, changeCallback, displayValueFn, autoConfig) {
        return {
            parentForm: form,
            formControlName: field,
            placeholder: 'Empty',
            label,
            ...(displayValueFn ? { displayValueFn } : {}),
            ...(savedCallback ? { inputSaved: e11FormEventHelper(savedCallback) } : {}),
            ...(changeCallback ? { inputChange: e11FormEventHelper(changeCallback) } : {}),
            ...(autoConfig ? { autocompleteConfig: autoConfig } : {}),
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormService, deps: [{ token: LocationStore }, { token: AddressFormStore }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AddressFormService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: LocationStore }, { type: AddressFormStore }, { type: i0.NgZone }] });
function formatAddressInline(address, keysToHide = []) {
    const { address: addr1, address2, city, state, zip, countryCode } = address;
    return [
        keysToHide.includes('address') ? undefined : addr1,
        keysToHide.includes('address2') ? undefined : address2,
        keysToHide.includes('city') ? undefined : city,
        keysToHide.includes('state') ? undefined : state,
        keysToHide.includes('zip') ? undefined : zip,
        keysToHide.includes('countryCode') ? undefined : countryCode,
    ]
        .filter((part) => part)
        .join(', ');
}

class ViewAddressComponent {
    constructor() {
        this.addressDisplay = '';
        this.hide = ['zip'];
        this.class = '';
    }
    ngOnInit() {
        this.addressDisplay = formatAddressInline(this.address, this.hide);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ViewAddressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: ViewAddressComponent, isStandalone: true, selector: "sdk-maps-view-address", inputs: { hide: "hide", address: "address", class: "class" }, ngImport: i0, template: ` <span [class]="class">{{ addressDisplay }}</span> `, isInline: true, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ViewAddressComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-maps-view-address',
                    standalone: true,
                    imports: [NgIf, NgForOf],
                    template: ` <span [class]="class">{{ addressDisplay }}</span> `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { hide: [{
                type: Input
            }], address: [{
                type: Input
            }], class: [{
                type: Input
            }] } });

/**
 * TODO: Map markers. Fit bounds
 */
class MapComponent {
    constructor(mapsLoaderService, logger) {
        this.mapsLoaderService = mapsLoaderService;
        this.logger = logger;
        this.center = { lat: 32.7767, lng: -96.797 };
        this.zoom = 10;
        this.locationClick = new EventEmitter();
    }
    async ngOnInit() {
        this.apiLoaded = this.mapsLoaderService.loadMaps();
    }
    /**
     * Todo expand to include other params
     * @param event
     */
    mapClicked(event) {
        const latLng = event.latLng?.toJSON();
        this.locationClick.emit(latLng);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapComponent, deps: [{ token: MapsLoaderService }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: MapComponent, selector: "e11-map", inputs: { center: "center", zoom: "zoom" }, outputs: { locationClick: "locationClick" }, ngImport: i0, template: "<div *ngIf=\"apiLoaded | async\">\n  <google-map [center]=\"center\" [zoom]=\"zoom\" (mapClick)=\"mapClicked($event)\">\n    <ng-content></ng-content>\n  </google-map>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: i3.GoogleMap, selector: "google-map", inputs: ["height", "width", "mapId", "mapTypeId", "center", "zoom", "options"], outputs: ["mapInitialized", "authFailure", "boundsChanged", "centerChanged", "mapClick", "mapDblclick", "mapDrag", "mapDragend", "mapDragstart", "headingChanged", "idle", "maptypeidChanged", "mapMousemove", "mapMouseout", "mapMouseover", "projectionChanged", "mapRightclick", "tilesloaded", "tiltChanged", "zoomChanged"], exportAs: ["googleMap"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i4.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-map', template: "<div *ngIf=\"apiLoaded | async\">\n  <google-map [center]=\"center\" [zoom]=\"zoom\" (mapClick)=\"mapClicked($event)\">\n    <ng-content></ng-content>\n  </google-map>\n</div>\n" }]
        }], ctorParameters: () => [{ type: MapsLoaderService }, { type: i2.E11Logger }], propDecorators: { center: [{
                type: Input
            }], zoom: [{
                type: Input
            }], locationClick: [{
                type: Output
            }] } });

class MapsComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: MapsComponent, selector: "lib-maps", ngImport: i0, template: ` <p>maps works!</p> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-maps', template: ` <p>maps works!</p> ` }]
        }], ctorParameters: () => [] });

const declareExportComponents = [MapComponent];
class MapsModule {
    static forRoot(config) {
        return {
            ngModule: MapsModule,
            providers: [{ provide: MAPS_CONFIG, useValue: config }],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: MapsModule, declarations: [MapsComponent, MapComponent], imports: [GoogleMapsModule, CommonModule, FormsModule, ReactiveFormsModule, ViewAddressComponent], exports: [MapsComponent, MapComponent, ViewAddressComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsModule, providers: [provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())], imports: [GoogleMapsModule, CommonModule, FormsModule, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MapsComponent, ...declareExportComponents],
                    exports: [MapsComponent, ...declareExportComponents, ViewAddressComponent],
                    imports: [GoogleMapsModule, CommonModule, FormsModule, ReactiveFormsModule, ViewAddressComponent],
                    providers: [provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())],
                }]
        }] });

class MapsService {
    constructor(logger) {
        this.logger = logger;
    }
    getLocationFromInput() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsService, deps: [{ token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MapsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i2.E11Logger }] });

/*
 * Public API Surface of maps
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AddressFormService, AddressFormStore, CountryService, LOCATION_ERRORS, LocationService, LocationStore, MAPS_CONFIG, MapComponent, MapsComponent, MapsLoaderService, MapsModule, MapsService, ViewAddressComponent, formatAddressInline, toE11GeolocationError };
